import React, { useState, useEffect, useRef } from "react";
import { apiService } from "../services/api";
import {
  User,
  UserWithProjectRole,
  ProjectInvitation,
  CreateInvitationRequest,
} from "../types/api";
import { InvitationPreviewModal } from "./InvitationPreviewModal";

interface AddMemberWidgetProps {
  projectId: number;
  projectTitle: string;
  allUsers: User[];
  projectUsers: UserWithProjectRole[];
  inviterName: string;
  inviterEmail: string;
  onUserAssigned: () => void;
}

export const AddMemberWidget: React.FC<AddMemberWidgetProps> = ({
  projectId,
  projectTitle,
  allUsers,
  projectUsers,
  inviterName,
  inviterEmail,
  onUserAssigned,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [mode, setMode] = useState<"assign" | "invite" | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [role, setRole] = useState("researcher");
  const [inviteExpiry, setInviteExpiry] = useState(168);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // The invite link is shown exactly once, right after creation: the server stores
  // only a hash of the token, so it cannot be retrieved again from the list.
  const [oneTimeInviteLink, setOneTimeInviteLink] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Derived values
  const availableUsers = allUsers.filter(
    (u) => !projectUsers.some((pu) => pu.id === u.id)
  );

  const suggestions = searchText.trim()
    ? availableUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(searchText.toLowerCase()) ||
          u.email.toLowerCase().includes(searchText.toLowerCase())
      )
    : [];

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(searchText.trim());

  // Check against ALL known users (org users + project users) to prevent
  // inviting someone who already has an account
  const emailLower = searchText.trim().toLowerCase();
  const existsInOrg = allUsers.some(
    (u) => u.email.toLowerCase() === emailLower
  );
  const existsInProject = projectUsers.some(
    (u) => u.email.toLowerCase() === emailLower
  );

  const showInviteOption =
    isValidEmail &&
    suggestions.length === 0 &&
    !existsInOrg &&
    !existsInProject &&
    mode === null;

  useEffect(() => {
    if (isOpen) {
      loadInvitations();
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const response = await apiService.listInvitations(projectId);
      setInvitations(response.invitations);
    } catch {
      // Silently fail — invitations are supplementary
    } finally {
      setInvitationsLoading(false);
    }
  };

  const resetForm = () => {
    setSearchText("");
    setMode(null);
    setSelectedUser(null);
    setRole("researcher");
    setInviteExpiry(168);
    setShowDropdown(false);
  };

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setSearchText(`${user.name} (${user.email})`);
    setMode("assign");
    setShowDropdown(false);
  };

  const handleSelectInvite = () => {
    setMode("invite");
    setShowDropdown(false);
  };

  const handleAssign = async () => {
    if (!selectedUser) return;
    try {
      setActionLoading(true);
      await apiService.adminAssignUserToProject(projectId, selectedUser.id, role);
      setMessage({ type: "success", text: `${selectedUser.name} added to project as ${role}.` });
      resetForm();
      onUserAssigned();
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to add user.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleInvite = () => {
    const email = searchText.trim();
    if (!email) return;
    setShowPreviewModal(true);
  };

  const handleInviteConfirm = async (customMessage: string) => {
    const email = searchText.trim();
    if (!email) return;
    setShowPreviewModal(false);
    try {
      setActionLoading(true);
      const request: CreateInvitationRequest = {
        email,
        role,
        expires_in_hours: inviteExpiry,
        custom_message: customMessage || undefined,
      };
      const created = await apiService.createInvitation(projectId, request);
      setOneTimeInviteLink(created.invite_url);
      setMessage({
        type: "success",
        text: `Invitation created for ${email}. Copy the link now — it cannot be shown again.`,
      });
      resetForm();
      await loadInvitations();
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to send invitation.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async (invitationId: number) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try {
      await apiService.revokeInvitation(invitationId);
      await loadInvitations();
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to revoke invitation.",
      });
    }
  };

  const copyOneTimeInviteLink = () => {
    if (!oneTimeInviteLink) return;
    navigator.clipboard.writeText(oneTimeInviteLink);
    setMessage({ type: "success", text: "Invitation link copied!" });
  };

  const activeInvitations = invitations.filter(
    (inv) => !inv.used_at && new Date(inv.expires_at) > new Date()
  );

  if (!isOpen) {
    return (
      <div style={{ marginBottom: "16px" }}>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 14px",
            background: "var(--fill-primary)",
            color: "var(--on-primary)",
            border: "none",
            borderRadius: "var(--radius)",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add member
        </button>
        {activeInvitations.length > 0 && (
          <span
            style={{
              marginLeft: "12px",
              fontSize: "13px",
              color: "var(--text-muted)",
            }}
          >
            {activeInvitations.length} pending invitation{activeInvitations.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#f8f9fa",
        padding: "16px",
        borderRadius: "8px",
        marginBottom: "16px",
        border: "1px solid #dee2e6",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h4 style={{ margin: 0 }}>Add Member to Project</h4>
        <button
          onClick={() => {
            setIsOpen(false);
            resetForm();
          }}
          style={{
            padding: "4px 12px",
            background: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "13px",
          }}
        >
          Close
        </button>
      </div>

      {/* Feedback message */}
      {message && (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: "4px",
            marginBottom: "12px",
            background: message.type === "success" ? "#d4edda" : "#f8d7da",
            color: message.type === "success" ? "#155724" : "#721c24",
            border: `1px solid ${message.type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
            fontSize: "14px",
          }}
        >
          {message.text}
        </div>
      )}

      {/* One-time invite link. Shown only immediately after creation — the server
          keeps just a hash of the token, so this link is unrecoverable afterwards. */}
      {oneTimeInviteLink && (
        <div
          style={{
            padding: "8px 12px",
            borderRadius: "4px",
            marginBottom: "12px",
            background: "#fff3cd",
            border: "1px solid #ffeeba",
            color: "#856404",
            fontSize: "13px",
          }}
        >
          <div style={{ marginBottom: "6px", fontWeight: 600 }}>
            Invite link (shown once)
          </div>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <input
              readOnly
              value={oneTimeInviteLink}
              onFocus={(e) => e.currentTarget.select()}
              style={{
                flex: 1,
                padding: "4px 6px",
                fontSize: "12px",
                fontFamily: "monospace",
                border: "1px solid #dee2e6",
                borderRadius: "3px",
              }}
            />
            <button
              onClick={copyOneTimeInviteLink}
              style={{
                padding: "4px 10px",
                background: "#e9ecef",
                border: "1px solid #dee2e6",
                borderRadius: "3px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Copy
            </button>
            <button
              onClick={() => setOneTimeInviteLink(null)}
              style={{
                padding: "4px 10px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "12px",
                color: "#856404",
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Search input + dropdown */}
      <div ref={dropdownRef} style={{ position: "relative", marginBottom: "12px" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            ref={inputRef}
            type="text"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              if (mode !== null) {
                // If user edits text after selecting, reset mode
                setMode(null);
                setSelectedUser(null);
              }
              setShowDropdown(true);
            }}
            onFocus={() => {
              if (mode === null && searchText.trim()) setShowDropdown(true);
            }}
            placeholder="Type a name or email address..."
            disabled={actionLoading}
            style={{
              flex: 1,
              padding: "8px 12px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
          {(searchText || mode) && (
            <button
              onClick={resetForm}
              disabled={actionLoading}
              style={{
                padding: "8px 12px",
                background: "white",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px",
                color: "#6c757d",
              }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Suggestions dropdown */}
        {showDropdown && mode === null && searchText.trim() && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: "1px solid #dee2e6",
              borderRadius: "0 0 4px 4px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              zIndex: 10,
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {suggestions.map((user) => (
              <div
                key={user.id}
                onClick={() => handleSelectUser(user)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: "14px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#e9ecef")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "white")
                }
              >
                <strong>{user.name}</strong>
                <span style={{ color: "#6c757d", marginLeft: "8px" }}>
                  {user.email}
                </span>
              </div>
            ))}
            {suggestions.length === 0 && !showInviteOption && (
              <div
                style={{
                  padding: "8px 12px",
                  color: "#6c757d",
                  fontSize: "14px",
                }}
              >
                {existsInProject
                  ? "This user is already a member of this project"
                  : existsInOrg
                    ? "No matching users available to add"
                    : !isValidEmail
                      ? "No matching users found — type a full email to send an invitation"
                      : "No matching users found"}
              </div>
            )}
            {showInviteOption && (
              <div
                onClick={handleSelectInvite}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  background: "#fff3cd",
                  borderTop: suggestions.length > 0 ? "1px solid #dee2e6" : "none",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#ffeeba")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fff3cd")
                }
              >
                <strong>{searchText.trim()}</strong> is not in your organization
                <span style={{ color: "#856404", marginLeft: "6px" }}>
                  — click to send invitation
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assign mode: role + button */}
      {mode === "assign" && selectedUser && (
        <div style={{ display: "flex", gap: "12px", alignItems: "end" }}>
          <div style={{ width: "180px" }}>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" }}
            >
              Role:
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
              }}
            >
              <option value="researcher">Researcher</option>
              <option value="pi">PI (Principal Investigator)</option>
            </select>
          </div>
          <button
            onClick={handleAssign}
            disabled={actionLoading}
            style={{
              padding: "8px 20px",
              background: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: actionLoading ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
          >
            {actionLoading ? "Adding..." : "Add to Project"}
          </button>
        </div>
      )}

      {/* Invite mode: role + expiry + button */}
      {mode === "invite" && (
        <div style={{ display: "flex", gap: "12px", alignItems: "end", flexWrap: "wrap" }}>
          <div style={{ width: "180px" }}>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" }}
            >
              Role:
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
              }}
            >
              <option value="researcher">Researcher</option>
              <option value="pi">PI (Principal Investigator)</option>
            </select>
          </div>
          <div style={{ width: "200px" }}>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" }}
            >
              Expires in:
            </label>
            <select
              value={inviteExpiry}
              onChange={(e) => setInviteExpiry(parseInt(e.target.value))}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
              }}
            >
              <option value={24}>24 hours (1 day)</option>
              <option value={72}>72 hours (3 days)</option>
              <option value={168}>168 hours (7 days)</option>
              <option value={336}>336 hours (14 days)</option>
              <option value={720}>720 hours (30 days)</option>
            </select>
          </div>
          <button
            onClick={handleInvite}
            disabled={actionLoading}
            style={{
              padding: "8px 20px",
              background: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: actionLoading ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
          >
            {actionLoading ? "Sending..." : "Send Invitation"}
          </button>
        </div>
      )}

      {/* Pending Invitations */}
      {activeInvitations.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <h5 style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#495057" }}>
            Pending Invitations
          </h5>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            {activeInvitations.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  background: "white",
                  border: "1px solid #e9ecef",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
              >
                <div>
                  <strong>{inv.email || "Open invite"}</strong>
                  <span style={{ color: "#6c757d", marginLeft: "8px" }}>
                    {inv.role}
                  </span>
                  <span style={{ color: "#adb5bd", marginLeft: "8px" }}>
                    expires {new Date(inv.expires_at).toLocaleDateString()}
                  </span>
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    onClick={() => handleRevoke(inv.id)}
                    style={{
                      padding: "3px 10px",
                      background: "#fff5f5",
                      border: "1px solid #f5c6cb",
                      borderRadius: "3px",
                      cursor: "pointer",
                      fontSize: "12px",
                      color: "#721c24",
                    }}
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {invitationsLoading && (
        <p style={{ fontSize: "13px", color: "#6c757d", marginTop: "8px" }}>
          Loading invitations...
        </p>
      )}

      {showPreviewModal && (
        <InvitationPreviewModal
          recipientEmail={searchText.trim()}
          defaultMessage=""
          inviterName={inviterName}
          inviterEmail={inviterEmail}
          systemEmail="nafnleyndarkerfi@hi.is"
          inviteType="project"
          projectTitle={projectTitle}
          onSend={handleInviteConfirm}
          onCancel={() => setShowPreviewModal(false)}
        />
      )}
    </div>
  );
};
